import { Money, MoneySchema } from "@redotech/money";
import {
  checkoutShippingOptionSchema_,
  LandedCostData,
} from "@redotech/redo-conversion-checkout-shipping-common/checkout-shipping-option";
import { sumMinorAmounts } from "@redotech/redo-conversion-checkout-shipping-common/landed-cost";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";
import { MercuryModelMode } from "../conversion/mercury-model";
import { getParcelsForCheckoutFromOmsAutomationResultSchema } from "./checkout-oms-automation-utils";

// Duties + import taxes + fees on the order's selected checkout options, summed
// in shop currency (vs getCustomerPaidLandedCost's presentment). Null if none/zero.
export function getLandedCostBreakdown(
  options:
    | readonly { landedCostData?: LandedCostData | null }[]
    | null
    | undefined,
): { duties: Money; taxes: Money; fees: Money } | null {
  const entries = (options ?? [])
    .map((option) => option.landedCostData)
    .filter((lcd): lcd is LandedCostData => lcd != null);
  const [first] = entries;
  if (!first) {
    return null;
  }
  // One selected option per order in practice; only sum entries sharing the
  // first entry's currency rather than risk adding across currencies.
  const { currency } = first;
  const inCurrency = entries.filter((lc) => lc.currency === currency);
  const duties = sumMinorAmounts(
    inCurrency.map((lc) => lc.duties),
    currency,
  );
  const taxes = sumMinorAmounts(
    inCurrency.map((lc) => lc.taxes),
    currency,
  );
  const fees = sumMinorAmounts(
    inCurrency.map((lc) => lc.fees),
    currency,
  );
  if (duties.amount <= 0 && taxes.amount <= 0 && fees.amount <= 0) {
    return null;
  }
  return { duties, taxes, fees };
}

export const featureValuePairSchema = z.array(
  z.tuple([z.string(), z.number()]),
);
export const optionWeightsSchema = z.record(z.string(), featureValuePairSchema);

const baseMercuryResultSchema = z.object({
  checkoutExperienceId: z.string(),
  checkoutTreeNodeId: z.string(),
  optimalPrices: z.record(z.string(), z.number()),
  labelCostShopMoneyByRateTableId: z.record(z.string(), MoneySchema).nullish(),
});

const mercuryLiteResultSchema_ = baseMercuryResultSchema.extend({
  modelMode: z.literal(MercuryModelMode.LITE_ENABLED),
});
export const mercuryLiteResultSchema: z.ZodType<MercuryLiteResult> =
  mercuryLiteResultSchema_;
export interface MercuryLiteResult extends z.infer<
  typeof mercuryLiteResultSchema_
> {}

const mercuryFullResultSchema = baseMercuryResultSchema.extend({
  modelMode: z.union([
    z.literal(MercuryModelMode.ENABLED),
    z.literal(MercuryModelMode.SHADOW),
  ]),
  expectedMargin: z.number(),
  expectedMarginsByRateTable: z.record(z.string(), z.number()),
  priceCombinationsExplored: z.number().nullish(),
  betaWeightsMean: optionWeightsSchema.nullish(),
  betaWeightsStd: optionWeightsSchema.nullish(),
  betaWeightsSampled: optionWeightsSchema.nullish(),
  featuresWithOptimalPrices: featureValuePairSchema.nullish(),
  inferenceTimeMs: z.number(),
});

const mercuryResultSchema_ = z.union([
  mercuryLiteResultSchema_,
  mercuryFullResultSchema,
]);
export const mercuryResultSchema: z.ZodType<MercuryResult> =
  mercuryResultSchema_;
export type MercuryResult = z.infer<typeof mercuryResultSchema_>;

export enum SelectedParcelSource {
  OMS_AUTOMATION = "OMS_AUTOMATION",
  PARCEL_TREE = "PARCEL_TREE",
  DEFAULT = "DEFAULT",
}

const parcelSelectionDecisionInfoSchema = z.union([
  z.object({
    source: z.literal(SelectedParcelSource.OMS_AUTOMATION),
    omsAutomationParcelResult:
      getParcelsForCheckoutFromOmsAutomationResultSchema,
  }),
  z.object({ source: z.literal(SelectedParcelSource.PARCEL_TREE) }),
  z.object({ source: z.literal(SelectedParcelSource.DEFAULT) }),
]);

const selectedParcelInfoSchema = z.object({
  selectedParcelIds: z.array(z.string()),
  parcelSelectionDecisionInfo: parcelSelectionDecisionInfoSchema,
});
export interface SelectedParcelInfo extends z.infer<
  typeof selectedParcelInfoSchema
> {}

const checkoutShippingOptionsDocumentSchema_ = z.object({
  _id: zExt.objectId(),
  stableId: z.string().optional(),
  checkoutToken: z.string().optional(),
  team: zExt.objectId(),
  options: z.array(checkoutShippingOptionSchema_),
  checkoutExperienceIdToMercuryModelId: z
    .record(z.string(), z.string().nullish())
    .nullish(),
  mercuryResults: z.array(mercuryResultSchema_).nullish(),
  selectedParcelInfoByRateTableId: z
    .record(z.string(), selectedParcelInfoSchema)
    .nullish(),
  /**
   * True when this document was persisted by the synchronous rates fallback
   * path. Fulfillment-time consumers (notably the
   * `applyCheckoutRatePurchased` Temporal activity) use this to skip applying
   * the synthesized carrier+service to the FulfillmentGroup, letting the OMS
   * pick the carrier naturally from real outbound rates instead.
   */
  fromFallback: z.boolean().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const checkoutShippingOptionsDocumentSchema: z.ZodType<CheckoutShippingOptionsDocument> =
  checkoutShippingOptionsDocumentSchema_;
export interface CheckoutShippingOptionsDocument extends z.infer<
  typeof checkoutShippingOptionsDocumentSchema_
> {}

/**
 * Input schema for creating a new document (without timestamps and _id)
 */
const createCheckoutShippingOptionsDocumentSchema_ =
  checkoutShippingOptionsDocumentSchema_.omit({
    _id: true,
    stableId: true,
    createdAt: true,
    updatedAt: true,
  });
export const createCheckoutShippingOptionsDocumentSchema: z.ZodType<CreateCheckoutShippingOptionsDocument> =
  createCheckoutShippingOptionsDocumentSchema_;
export interface CreateCheckoutShippingOptionsDocument extends z.infer<
  typeof createCheckoutShippingOptionsDocumentSchema_
> {}
